import { useEffect, useMemo, useRef, useState } from 'react';
import { FloppyDisk, Trash, X, ChatText, Plus, Info, CheckSquare, CircleNotch, Warning, ShieldCheck, Files, CaretDown, FloppyDiskBack, ArrowSquareOut, LinkSimple, Check, Trophy } from '@phosphor-icons/react';
import { PokerPanel } from './PokerPanel';
import { useAppStore } from '@/store/appStore';
import {
  useCatalogs, useChecklist, useTasks,
  useCreateTask, useDeleteTask, useTaskDetail, useUpdateTask
} from '@/hooks/useTasks';
import { useTaskTimeline } from '@/hooks/useTimeline';
import { TimelineList } from '@/components/timeline/TimelineList';
import { api } from '@/api/client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ChecklistTipo } from '@/types';
import { StickyCommentBar } from './StickyCommentBar';
import { AutorizadoInternoField } from './AutorizadoInternoField';
import { DateFieldWithClear } from '@/components/common/DateFieldWithClear';
import { StatusSelect } from '@/components/common/StatusSelect';
import { SearchableSelect } from '@/components/common/SearchableSelect';
import { SegmentedControl } from '@/components/common/SegmentedControl';
import { fmtDate } from '@/lib/utils';
import { getCurrentUserEmail } from '@/auth/getCurrentUser';
import { ESTADO_IDS } from '@/constants/estadoIds';
import type { CreateTaskDto, UpdateTaskDto } from '@/types';

// Snapshot del state inicial del form. Se captura una vez por apertura
// (en EDIT después de hidratar desde task; en CREATE después de aplicar
// los defaults). Comparar vs los state vars actuales nos da isDirty.
type FormSnapshot = {
  nombreTarea: string;
  descripcion: string;
  observacion: string;
  idProyecto: number | null;
  idCategoria: number | null;
  idEstado: number | null;
  prioridad: number | null;
  responsable: number | null;
  idCaso: number | null;
  idLote: number | null;
  idBloque: number | null;
  idModelo: number | null;
  // Tarea padre (jerarquia para el Gantt). null = raiz.
  idTareaPadre: number | null;
  idSolicitud: string;
  idSolicitante: number | null;
  fechaInicio: string;
  fechaProyectada: string;
  // Inicio planeado (par de fechaProyectada).
  fechaProyectadaIn: string;
  fechaCompletado: string;
  linkEntrega: string;
  idAutorizadoInt: number | null;
  fechaAutorizadoInt: string;
  esActivo: boolean;
  // Sprint + horas: campos nuevos del feature F1. idSprint default al sprint
  // actual cuando se crea (se decide en el effect de defaults); horas como
  // string para tolerar input intermedio antes de parsear a decimal.
  idSprint: number | null;
  horasEstimadas: string;
};

// IDCol del unico usuario habilitado para ver/editar el toggle EsActivo
// desde el modal (soft-delete de tareas). Hardcoded por ahora, request de
// Daniel; si en el futuro se abre a mas roles, mover a un permission helper.
const ES_ACTIVO_TOGGLE_IDCOL = 26;

// Encabezado de sección dentro del form: mini-título + línea. Separa grupos
// conceptuales (los campos que arman el nombre vs los de gestión).
function SectionDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 pt-1">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-brand-gray-400">
        {label}
      </span>
      <div className="flex-1 border-t border-stone-200" />
    </div>
  );
}

export function TaskModal() {
  const taskId = useAppStore(s => s.selectedTaskId);
  const isCreating = useAppStore(s => s.isCreatingTask);
  const closeModal = useAppStore(s => s.selectTask);
  const setCreating = useAppStore(s => s.setCreatingTask);
  const setPendingStatusChange = useAppStore(s => s.setPendingStatusChange);

  const open = taskId !== null || isCreating;
  if (!open) return null;
  return <ModalContent onClose={() => {
    closeModal(null);
    setCreating(false);
    // Si el usuario cancela antes de que la hidratación haya consumido un
    // pending pendiente (ej. fetch de la task lenta), no queremos que quede
    // colgado y se aplique a la próxima apertura de OTRA tarea.
    setPendingStatusChange(null);
  }} />;
}

function ModalContent({ onClose }: { onClose: () => void }) {
  const taskId = useAppStore(s => s.selectedTaskId);
  const isCreating = useAppStore(s => s.isCreatingTask);
  const qc = useQueryClient();

  const { data: catalogs } = useCatalogs();
  const { data: task } = useTaskDetail(taskId);
  const { data: checklist = [] } = useChecklist(taskId);
  // Lista completa de tareas (cacheada, compartida con kanban/tabla). La usamos
  // para poblar el selector de "Tarea padre" y para calcular los descendientes
  // de la tarea actual (para excluirlos y no permitir ciclos en la jerarquia).
  const { data: allTasks = [] } = useTasks('status', '');
  // El timeline se carga adentro del TimelineForTask wrapper para no
  // reactivarse cuando el modal sigue abierto en CREATE. Aca solo necesitamos
  // el taskId para invalidar el query tras agregar un comentario.

  const update = useUpdateTask();
  const create = useCreateTask();
  const remove = useDeleteTask();

  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Campos editables
  const [nombreTarea, setNombreTarea] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [observacion, setObservacion] = useState('');
  const [idProyecto, setIdProyecto] = useState<number | null>(null);
  const [idCategoria, setIdCategoria] = useState<number | null>(null);
  const [idEstado, setIdEstado] = useState<number | null>(null);
  const [prioridad, setPrioridad] = useState<number | null>(null);
  const [responsable, setResponsable] = useState<number | null>(null);
  const [idCaso, setIdCaso] = useState<number | null>(null);
  const [idLote, setIdLote] = useState<number | null>(null);
  const [idBloque, setIdBloque] = useState<number | null>(null);
  const [idModelo, setIdModelo] = useState<number | null>(null);
  const [idTareaPadre, setIdTareaPadre] = useState<number | null>(null);
  const [idSolicitud, setIdSolicitud] = useState<string>('');
  const [idSolicitante, setIdSolicitante] = useState<number | null>(null);
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaProyectada, setFechaProyectada] = useState('');
  const [fechaProyectadaIn, setFechaProyectadaIn] = useState('');
  const [fechaCompletado, setFechaCompletado] = useState('');
  const [fechaAutorizadoInt, setFechaAutorizadoInt] = useState('');
  const [idAutorizadoInt, setIdAutorizadoInt] = useState<number | null>(null);
  const [linkEntrega, setLinkEntrega] = useState('');
  const [esActivo, setEsActivo] = useState(true);
  const [idSprint, setIdSprint] = useState<number | null>(null);
  const [horasEstimadas, setHorasEstimadas] = useState('');
  // Parte personalizada del nombre concatenado + flag "nombre libre" (escribir
  // el nombre completo a mano, sin concatenación). Ver computedName más abajo.
  const [custom, setCustom] = useState('');
  const [nombreLibre, setNombreLibre] = useState(false);

  // Modo "agregar link entrega" inline desde el row de tabs. tempLink mantiene
  // el valor mientras se tipea, y al aplicar se vuelca a setLinkEntrega (estado
  // del form; persiste con el Guardar normal). No es un PATCH directo para no
  // chocar con otros campos dirty del modal.
  const [addingLink, setAddingLink] = useState(false);
  const [tempLink, setTempLink] = useState('');

  const [newComment, setNewComment] = useState('');

  // Tab activo del modal. Mantenemos los 3 tabs siempre montados (con
  // display:none los inactivos) para que el state interno de cada uno
  // (cursores en inputs, scroll position) no se pierda al cambiar de tab.
  type ModalTab = 'detalles' | 'revision' | 'poker';
  const [activeTab, setActiveTab] = useState<ModalTab>('detalles');

  // Hidratamos los inputs UNA SOLA VEZ por taskId. Necesario porque:
  //  (1) React.StrictMode ejecuta cada efecto dos veces en mount (en dev), y la
  //      segunda corrida pisaba idEstado de vuelta al valor original de task,
  //      después de que la primera ya había consumido el pendingStatusChange.
  //  (2) react-query devuelve el task cacheado sincrónicamente al re-abrir,
  //      así que ambas corridas de StrictMode caen en if(task) y reinicializan
  //      los inputs (perdiendo cualquier override del pending).
  // El ref guarda el id ya hidratado y hace la hidratación idempotente.
  const hydratedForTaskRef = useRef<number | null>(null);
  // Snapshot del form al momento de hidratar. Sirve para detectar isDirty.
  // Se reinicializa a null cuando ModalContent desmonta (al cerrar el modal).
  const initialSnapshotRef = useRef<FormSnapshot | null>(null);

  useEffect(() => {
  if (task && hydratedForTaskRef.current !== task.idTareaArq) {
    hydratedForTaskRef.current = task.idTareaArq;

    // Resolvemos el idEstado efectivo arriba para usarlo en el setter Y en el
    // snapshot. Si el drag-and-drop dejó un cambio pendiente para esta tarea,
    // el estado del form arranca en el nuevo (sino el modal abriría "dirty").
    const pending = useAppStore.getState().pendingStatusChange;
    const pendingApplies = pending !== null && pending.taskId === task.idTareaArq;
    const effectiveIdEstado = pendingApplies
      ? pending!.newStatusId
      : (task.idEstado ?? null);

    // Regla: una tarea en Hielera NO lleva revisor. Al hidratar una tarea en
    // Hielera arrancamos sin Autorizado Interno (y el snapshot también, para no
    // abrir el modal "sucio"). El backend además lo limpia en cada escritura.
    const hieleraAlHidratar = effectiveIdEstado === ESTADO_IDS.HIELERA;
    const hidIdAutorizado = hieleraAlHidratar ? null : (task.idAutorizadoInt ?? null);
    const hidFechaAutorizado = hieleraAlHidratar ? '' : (task.fechaAutorizadoInt ? task.fechaAutorizadoInt.slice(0, 10) : '');

    setNombreTarea(task.nombreTarea || '');
    setDescripcion(task.descripcion || '');
    setObservacion(task.observacion || '');
    setIdProyecto(task.idProyecto ?? null);
    setIdCategoria(task.idCategoria ?? null);
    setIdEstado(effectiveIdEstado);
    setPrioridad(task.prioridad ?? null);
    setResponsable(task.responsable ?? null);
    setIdCaso(task.idCaso ?? null);
    setIdLote(task.idLote ?? null);
    setIdBloque(task.idBloque ?? null);
    setIdModelo(task.idModelo ?? null);
    setIdTareaPadre(task.idTareaPadre ?? null);
    setIdSolicitud(task.idSolicitud ? String(task.idSolicitud) : '');
    setIdSolicitante(task.idSolicitante ?? null);
    setFechaInicio(task.fechaInicio ? task.fechaInicio.slice(0, 10) : '');
    setFechaProyectada(task.fechaProyectada ? task.fechaProyectada.slice(0, 10) : '');
    setFechaProyectadaIn(task.fechaProyectadaIn ? task.fechaProyectadaIn.slice(0, 10) : '');
    setFechaCompletado(task.fechaCompletado ? task.fechaCompletado.slice(0, 10) : '');
    setFechaAutorizadoInt(hidFechaAutorizado);
    setIdAutorizadoInt(hidIdAutorizado);
    setLinkEntrega(task.linkEntrega || '');
    setEsActivo(task.esActivo ?? true);
    setIdSprint(task.idSprint ?? null);
    setHorasEstimadas(task.horasEstimadas != null ? String(task.horasEstimadas) : '');
    // Custom + modo. Legacy (sin nombreCustom) arranca en "nombre libre" para
    // NO recomponer/pisar el nombre existente; las nuevas recomponen en vivo.
    setCustom(task.nombreCustom ?? '');
    setNombreLibre(task.nombreCustom == null);

    if (pendingApplies) {
      useAppStore.getState().setPendingStatusChange(null);
    }

    // Snapshot DESPUÉS de aplicar el pending: refleja el state efectivo.
    initialSnapshotRef.current = {
      nombreTarea: task.nombreTarea || '',
      descripcion: task.descripcion || '',
      observacion: task.observacion || '',
      idProyecto: task.idProyecto ?? null,
      idCategoria: task.idCategoria ?? null,
      idEstado: effectiveIdEstado,
      prioridad: task.prioridad ?? null,
      responsable: task.responsable ?? null,
      idCaso: task.idCaso ?? null,
      idLote: task.idLote ?? null,
      idBloque: task.idBloque ?? null,
      idModelo: task.idModelo ?? null,
      idTareaPadre: task.idTareaPadre ?? null,
      idSolicitud: task.idSolicitud ? String(task.idSolicitud) : '',
      idSolicitante: task.idSolicitante ?? null,
      fechaInicio: task.fechaInicio ? task.fechaInicio.slice(0, 10) : '',
      fechaProyectada: task.fechaProyectada ? task.fechaProyectada.slice(0, 10) : '',
      fechaProyectadaIn: task.fechaProyectadaIn ? task.fechaProyectadaIn.slice(0, 10) : '',
      fechaCompletado: task.fechaCompletado ? task.fechaCompletado.slice(0, 10) : '',
      linkEntrega: task.linkEntrega || '',
      idAutorizadoInt: hidIdAutorizado,
      fechaAutorizadoInt: hidFechaAutorizado,
      esActivo: task.esActivo ?? true,
      idSprint: task.idSprint ?? null,
      horasEstimadas: task.horasEstimadas != null ? String(task.horasEstimadas) : '',
    };
  } else if (isCreating && catalogs && idEstado === null) {
    // Default state on creation: "Hielera" (parking/icebox).
    // Architecture team workflow: tasks usually need triage before being
    // queued for active work; Hielera better reflects "not yet decided".
    const def = catalogs.statuses.find(s => s.idEst === ESTADO_IDS.HIELERA) ?? catalogs.statuses[0];
    const defaultIdEstado = def?.idEst ?? null;
    if (defaultIdEstado !== null) setIdEstado(defaultIdEstado);

    // CREATE arranca con el nombre concatenado (no libre) y Custom vacío.
    setCustom('');
    setNombreLibre(false);

    // CREATE arranca en Hielera (default). Por pedido: una tarea nueva en
    // Hielera NO trae responsable ni revisor por defecto — se asignan a mano
    // cuando sale de Hielera. (Antes se autocompletaba el responsable con el
    // usuario logueado; se quitó.)
    const defaultResponsable: number | null = null;

    // Sprint default: el semanal vigente (FechaInicio <= hoy <= FechaFin).
    // Blindaje: si no hay vigente (ej. el Timer del lunes no corrió y quedó un
    // hueco), NO caemos a un sprint viejo ni directo a "Sin Asignar" — elegimos
    // el semanal más reciente que YA empezó (FechaInicio <= hoy). Solo si no hay
    // ningún semanal aplicable usamos "Sin Asignar". Mantiene CREATE consistente
    // cuando el usuario no toca el campo; si el server resuelve distinto el
    // valor final lo pisa al volver el response.
    const today = new Date().toISOString().slice(0, 10);
    const semanales = catalogs.sprints.filter((s) => s.tipo === 'Semanal' && s.activo);
    const semanalVigente = semanales.find(
      (s) => s.fechaInicio && s.fechaFin && s.fechaInicio.slice(0, 10) <= today && s.fechaFin.slice(0, 10) >= today
    );
    // Más reciente que ya empezó (mayor FechaInicio <= hoy).
    const semanalReciente = semanales
      .filter((s) => s.fechaInicio && s.fechaInicio.slice(0, 10) <= today)
      .sort((a, b) => (b.fechaInicio ?? '').localeCompare(a.fechaInicio ?? ''))[0];
    const sinAsignar = catalogs.sprints.find((s) => s.tipo === 'SinAsignar');
    const defaultSprintId =
      semanalVigente?.idSprint ?? semanalReciente?.idSprint ?? sinAsignar?.idSprint ?? null;
    if (idSprint === null && defaultSprintId !== null) setIdSprint(defaultSprintId);

    // Snapshot DESPUÉS de aplicar los defaults — el modal CREATE arranca clean.
    initialSnapshotRef.current = {
      nombreTarea: '',
      descripcion: '',
      observacion: '',
      idProyecto: null,
      idCategoria: null,
      idEstado: defaultIdEstado,
      prioridad: null,
      responsable: defaultResponsable,
      idCaso: null,
      idLote: null,
      idBloque: null,
      idModelo: null,
      idTareaPadre: null,
      idSolicitud: '',
      idSolicitante: null,
      fechaInicio: '',
      fechaProyectada: '',
      fechaProyectadaIn: '',
      fechaCompletado: '',
      linkEntrega: '',
      idAutorizadoInt: null,
      fechaAutorizadoInt: '',
      esActivo: true,
      idSprint: defaultSprintId,
      horasEstimadas: '',
    };
  }
}, [task, isCreating, catalogs]);

  const isSaving = create.isPending || update.isPending;
  const isDeleting = remove.isPending;
  const isBusy = isSaving || isDeleting;
  // Qué estados requieren FechaCompletado lo decide el backend en el catálogo
  // (StatusDto.requiresFechaCompletado). Antes esto se hacía por string-match
  // contra "Completado" / "Revision"; ahora la regla vive en EstadoIds.cs.
  const currentStatus = catalogs?.statuses.find(s => s.idEst === idEstado);
  // Las tareas en Hielera no llevan revisor (Autorizado Interno): el campo se
  // muestra read-only y vacío mientras esté en Hielera.
  const esHielera = idEstado === ESTADO_IDS.HIELERA;
  const requiresFechaCompletado = currentStatus?.requiresFechaCompletado ?? false;
  const showFechaCompletado = requiresFechaCompletado;
  const fechaCompletadoMissing = requiresFechaCompletado && !fechaCompletado;
  const isInvalid = fechaCompletadoMissing;

  const isDirty = useMemo(() => {
    const snap = initialSnapshotRef.current;
    if (!snap) return false;
    return (
      nombreTarea !== snap.nombreTarea ||
      descripcion !== snap.descripcion ||
      observacion !== snap.observacion ||
      idProyecto !== snap.idProyecto ||
      idCategoria !== snap.idCategoria ||
      idEstado !== snap.idEstado ||
      prioridad !== snap.prioridad ||
      responsable !== snap.responsable ||
      idCaso !== snap.idCaso ||
      idLote !== snap.idLote ||
      idBloque !== snap.idBloque ||
      idModelo !== snap.idModelo ||
      idTareaPadre !== snap.idTareaPadre ||
      idSolicitud !== snap.idSolicitud ||
      idSolicitante !== snap.idSolicitante ||
      fechaInicio !== snap.fechaInicio ||
      fechaProyectada !== snap.fechaProyectada ||
      fechaProyectadaIn !== snap.fechaProyectadaIn ||
      fechaCompletado !== snap.fechaCompletado ||
      linkEntrega !== snap.linkEntrega ||
      idAutorizadoInt !== snap.idAutorizadoInt ||
      fechaAutorizadoInt !== snap.fechaAutorizadoInt ||
      esActivo !== snap.esActivo ||
      idSprint !== snap.idSprint ||
      horasEstimadas !== snap.horasEstimadas
    );
  }, [
    nombreTarea, descripcion, observacion,
    idProyecto, idCategoria, idEstado, prioridad, responsable,
    idCaso, idLote, idBloque, idModelo, idTareaPadre, idSolicitud, idSolicitante,
    fechaInicio, fechaProyectada, fechaProyectadaIn, fechaCompletado,
    linkEntrega,
    idAutorizadoInt, fechaAutorizadoInt,
    esActivo,
    idSprint, horasEstimadas,
  ]);

  function handleSave() {
    setErrorMsg(null);

    if (!nombreTarea.trim()) {
      setErrorMsg('El nombre de la tarea es requerido');
      return;
    }
    if (idEstado === null) {
      setErrorMsg('El estado es requerido');
      return;
    }

    // Horas estimadas: parseFloat tolera coma/punto y trim. Si esta vacio
    // mando null para limpiar la columna en EDIT (en CREATE el backend lo
    // acepta como "no estimado"). NaN -> null (input invalido).
    const horasParsed = horasEstimadas.trim() === ''
      ? null
      : (() => {
          const n = parseFloat(horasEstimadas.replace(',', '.'));
          return isNaN(n) ? null : n;
        })();

    const payload: CreateTaskDto = {
      nombreTarea,
      // null en modo "nombre libre" (no se recompone); sino el texto Custom.
      nombreCustom: nombreLibre ? null : custom,
      descripcion, observacion,
      idProyecto, idCategoria, idEstado, prioridad, responsable,
      idCaso,
      idLote,
      idBloque,
      idModelo,
      idTareaPadre,
      idSolicitud: idSolicitud ? parseInt(idSolicitud) : null,
      idSolicitante,
      fechaInicio: fechaInicio || null,
      fechaProyectada: fechaProyectada || null,
      fechaProyectadaIn: fechaProyectadaIn || null,
      // When status moves away from Completado/Revision, explicitly null the date
      // so backend recalculates TgCompletado=false from the date being absent.
      fechaCompletado: showFechaCompletado ? (fechaCompletado || null) : null,
      fechaAutorizadoInt: fechaAutorizadoInt || null,
      idAutorizadoInt,
      linkEntrega,
      esActivo,
      idSprint,
      horasEstimadas: horasParsed,
    };

    if (isCreating) {
      create.mutate(payload, {
        onSuccess: () => onClose(),
        onError: (err: any) => setErrorMsg(err?.message || 'Error al crear la tarea')
      });
    } else if (taskId !== null) {
      const upd: UpdateTaskDto = payload;
      update.mutate({ id: taskId, dto: upd }, {
        onSuccess: () => onClose(),
        onError: (err: any) => setErrorMsg(err?.message || 'Error al actualizar la tarea')
      });
    }
  }

  function handleDelete() {
    if (taskId === null) return;
    if (!confirm('¿Eliminar esta tarea?')) return;
    setErrorMsg(null);
    remove.mutate(taskId, {
      onSuccess: () => onClose(),
      onError: (err: any) => setErrorMsg(err?.message || 'Error al eliminar')
    });
  }

  function handleCloseAttempt() {
    if (isBusy) return;
    if (isDirty) {
      const confirmed = window.confirm('Tenés cambios sin guardar. ¿Salir igual?');
      if (!confirmed) return;
    }
    onClose();
  }

  // Tecla Escape cierra el modal con la misma lógica de confirm que la X / backdrop.
  // Si hay un popup abierto adentro del modal (DateFieldWithClear o StatusSelect),
  // dejamos que ese popup procese el Escape primero — la próxima presión cierra
  // el modal. Sin esto, una sola presión cerraría popup Y modal a la vez.
  useEffect(() => {
    function handleEscape(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      if (document.querySelector('.rdp-root, [role="listbox"]')) return;
      if (isBusy) return;
      if (isDirty) {
        const confirmed = window.confirm('Tenés cambios sin guardar. ¿Salir igual?');
        if (!confirmed) return;
      }
      onClose();
    }
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isBusy, isDirty, onClose]);

  async function postComment() {
    if (taskId === null || !newComment.trim()) return;
    await api.bitacora.create(taskId, newComment);
    setNewComment('');
    qc.invalidateQueries({ queryKey: ['bitacora', taskId] });
    qc.invalidateQueries({ queryKey: ['timeline', taskId] });
    qc.invalidateQueries({ queryKey: ['feed'] });
    qc.invalidateQueries({ queryKey: ['tasks'] });
  }

  async function toggleItem(itemId: number, completado: boolean) {
    if (taskId === null) return;

    // Detectamos ANTES del PATCH si este toggle es el ultimo item de
    // revision pendiente. Lo hacemos pre-mutation contra el state actual
    // para no depender de un refetch (mas confiable, sin race con cache).
    // Solo cuenta cuando se MARCA (no cuando se desmarca).
    const willBeLastRevisionDone = (() => {
      if (!completado) return false;
      const item = checklist.find(c => c.idChecklistArq === itemId);
      if (item?.tipo !== 'revision') return false;
      const otherRevisionItems = checklist.filter(
        c => c.tipo === 'revision' && c.idChecklistArq !== itemId
      );
      return otherRevisionItems.every(c => c.completado === true);
    })();

    await api.checklist.update(taskId, itemId, { completado });
    qc.invalidateQueries({ queryKey: ['checklist', taskId] });
    qc.invalidateQueries({ queryKey: ['tasks'] });

    if (!willBeLastRevisionDone) return;
    if (idEstado === ESTADO_IDS.COMPLETADO) return; // ya esta en Completado

    // Confirm + setear state local. El usuario aprieta Guardar para persistir.
    // No hacemos PATCH directo aca para no chocar con cambios pendientes en
    // otros campos (el modal ya tiene el indicador "Cambios sin guardar").
    const confirmed = window.confirm(
      'Todos los items de revisión están marcados. ¿Pasar la tarea a Completado?'
    );
    if (!confirmed) return;

    const today = new Date().toISOString().slice(0, 10);
    setIdEstado(ESTADO_IDS.COMPLETADO);
    if (!fechaCompletado) setFechaCompletado(today);
    if (!fechaAutorizadoInt) setFechaAutorizadoInt(today);
  }

  async function deleteItem(itemId: number) {
    if (taskId === null) return;
    if (!window.confirm('¿Eliminar este item del checklist?')) return;
    await api.checklist.remove(taskId, itemId);
    qc.invalidateQueries({ queryKey: ['checklist', taskId] });
    qc.invalidateQueries({ queryKey: ['tasks'] });
  }

  // Agrega un item al checklist con el tipo dado ('checklist' o 'revision').
  // Reemplaza al patron viejo de window.prompt por una sola UX para los dos
  // tabs.
  async function addChecklistItemOfType(tipo: 'checklist' | 'revision') {
    if (taskId === null) return;
    const text = window.prompt(
      tipo === 'revision' ? 'Texto del item de revisión' : 'Texto del checklist'
    );
    if (!text) return;
    await api.checklist.create(taskId, text, { tipo });
    qc.invalidateQueries({ queryKey: ['checklist', taskId] });
    qc.invalidateQueries({ queryKey: ['tasks'] });
  }

  // Particion del checklist por tipo. Memo para no recalcular en cada render.
  const checklistNormal = useMemo(
    () => checklist.filter(c => c.tipo !== 'revision'),
    [checklist]
  );
  const checklistRevision = useMemo(
    () => checklist.filter(c => c.tipo === 'revision'),
    [checklist]
  );

  // Round-robin del autorizador en CREATE: cuando se elige un Responsable
  // y no hay autorizador, pedimos al backend el candidato con menor carga
  // (excluyendo al responsable). Solo en CREATE — en EDIT respetamos lo
  // que esta guardado y los cambios manuales del usuario.
  // El ref evita re-pedir cuando el usuario borra manualmente el sugerido
  // (se sugiere una vez por valor de responsable).
  const lastSuggestedForResponsableRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isCreating) return;
    // Hielera no lleva revisor: no autosugerimos.
    if (idEstado === ESTADO_IDS.HIELERA) return;
    if (responsable === null) return;
    if (idAutorizadoInt !== null) return;
    if (lastSuggestedForResponsableRef.current === responsable) return;

    lastSuggestedForResponsableRef.current = responsable;
    const ac = new AbortController();
    api.tasks.suggestAuthorizer(responsable, ac.signal)
      .then(r => {
        if (r.idCol !== null) setIdAutorizadoInt(r.idCol);
      })
      .catch(() => { /* ignorar (abort u otros) */ });
    return () => ac.abort();
  }, [isCreating, responsable, idAutorizadoInt, idEstado]);

  // Regla: Hielera no lleva revisor. Cuando el estado pasa a Hielera (en vivo,
  // o vía el pending del kanban) limpiamos Autorizado Interno + su fecha. Al
  // hidratar una tarea ya en Hielera no marca "dirty" porque la hidratación ya
  // los dejó vacíos (ver arriba). El backend lo refuerza en cada escritura.
  useEffect(() => {
    if (idEstado !== ESTADO_IDS.HIELERA) return;
    if (idAutorizadoInt !== null) setIdAutorizadoInt(null);
    if (fechaAutorizadoInt !== '') setFechaAutorizadoInt('');
  }, [idEstado, idAutorizadoInt, fechaAutorizadoInt]);

  // Resuelve el IDCol del usuario logueado mirando catalogs.users por email.
  // Lo usamos para decidir quien puede editar Observacion (solo el Autorizado
  // Interno asignado a la tarea). En CREATE no hay autorizador todavia, asi
  // que el campo queda bloqueado hasta que se asigne y se guarde.
  const currentUserIdCol = useMemo(() => {
    if (!catalogs) return null;
    const myEmail = getCurrentUserEmail();
    if (!myEmail) return null;
    const me = catalogs.users.find(u => u.correo?.toLowerCase() === myEmail.toLowerCase());
    return me?.idCol ?? null;
  }, [catalogs]);

  const canEditObservacion =
    !isCreating && idAutorizadoInt !== null && idAutorizadoInt === currentUserIdCol;
  const observacionDisabledReason = isCreating
    ? 'Guardá la tarea y asigná un Autorizado Interno antes de editar Observación'
    : idAutorizadoInt === null
      ? 'Solo el Autorizado Interno puede editar las Observaciones'
      : 'Solo el Autorizado Interno puede editar las Observaciones';

  // Cascada de filtros: cuando cambia un campo padre, limpiar los hijos
  // si su valor actual ya no aplica. handleProyectoChange propaga vía
  // handleBloqueChange/handleLoteChange para no duplicar reglas.
  function handleLoteChange(newLote: number | null) {
    setIdLote(newLote);
    if (idCaso !== null) {
      const c = catalogs?.cases.find(cc => cc.idCaso === idCaso);
      if (!c || (newLote !== null && c.idLote !== newLote)) {
        setIdCaso(null);
      }
    }
  }

  function handleBloqueChange(newBloque: number | null) {
    setIdBloque(newBloque);
    if (idLote !== null) {
      const lot = catalogs?.lots.find(l => l.idLote === idLote);
      if (!lot || (newBloque !== null && lot.idBloque !== newBloque)) {
        handleLoteChange(null);
      }
    }
  }

  function handleProyectoChange(newProyecto: number | null) {
    setIdProyecto(newProyecto);
    if (idBloque !== null) {
      const block = catalogs?.blocks.find(b => b.idBloque === idBloque);
      if (!block || (newProyecto !== null && block.idProyecto !== newProyecto)) {
        handleBloqueChange(null);
        return;
      }
    }
    if (idLote !== null) {
      const lot = catalogs?.lots.find(l => l.idLote === idLote);
      if (!lot || (newProyecto !== null && lot.idProyecto !== newProyecto)) {
        handleLoteChange(null);
        return;
      }
    }
    if (idCaso !== null) {
      const c = catalogs?.cases.find(cc => cc.idCaso === idCaso);
      if (!c || (newProyecto !== null && c.idProyecto !== newProyecto)) {
        setIdCaso(null);
      }
    }
  }

  // Opciones filtradas para los combos en cascada.
  const blockOptions = useMemo(() => {
    const all = catalogs?.blocks ?? [];
    return (idProyecto === null ? all : all.filter(b => b.idProyecto === idProyecto))
      .map(b => ({ value: b.idBloque, label: b.bloque || `#${b.idBloque}` }));
  }, [catalogs?.blocks, idProyecto]);

  const lotOptions = useMemo(() => {
    const all = catalogs?.lots ?? [];
    return all
      .filter(l => idProyecto === null || l.idProyecto === idProyecto)
      .filter(l => idBloque === null || l.idBloque === idBloque)
      .map(l => ({ value: l.idLote, label: l.idBd || `#${l.idLote}` }));
  }, [catalogs?.lots, idProyecto, idBloque]);

  const caseOptions = useMemo(() => {
    const all = catalogs?.cases ?? [];
    return all
      .filter(c => idProyecto === null || c.idProyecto === idProyecto)
      .filter(c => idLote === null || c.idLote === idLote)
      // Orden alfabetico ascendente por LoteCliente; casos sin label van al final.
      .slice()
      .sort((a, b) => {
        const la = a.loteCliente ?? '';
        const lb = b.loteCliente ?? '';
        if (!la && !lb) return 0;
        if (!la) return 1;
        if (!lb) return -1;
        return la.localeCompare(lb, 'es', { numeric: true, sensitivity: 'base' });
      });
  }, [catalogs?.cases, idProyecto, idLote]);

  // Mapa idLote -> IDBD para etiquetar los casos con el código del lote
  // (V_CASOS no expone IDBD; lo resolvemos desde el catálogo de lotes).
  const idBdByLote = useMemo(() => {
    const m = new Map<number, string>();
    for (const l of catalogs?.lots ?? []) {
      if (l.idBd) m.set(l.idLote, l.idBd);
    }
    return m;
  }, [catalogs?.lots]);

  // Modelos filtrados por el proyecto elegido. El catálogo trae un flag de
  // actividad por proyecto (activoVN/VI/VB/VC); se cruza contra la abreviatura
  // del proyecto. Sin proyecto (o abreviatura desconocida) muestra todos.
  const modeloOptions = useMemo(() => {
    const all = catalogs?.modelos ?? [];
    const abrev = catalogs?.projects.find(p => p.idProyecto === idProyecto)?.abreviatura?.toUpperCase();
    const flagFor = (m: typeof all[number]) => {
      switch (abrev) {
        case 'VN': return m.activoVN;
        case 'VI': return m.activoVI;
        case 'VB': return m.activoVB;
        case 'VC': return m.activoVC;
        default:   return true; // sin proyecto o abreviatura no mapeada: todos
      }
    };
    return all
      .filter(flagFor)
      .map(m => ({ value: m.idModelo, label: m.nombre || `#${m.idModelo}` }));
  }, [catalogs?.modelos, catalogs?.projects, idProyecto]);

  // Candidatos a "Tarea padre": tareas activas del MISMO proyecto, excluyendo
  // la propia tarea y sus descendientes (para que la UI no permita armar un
  // ciclo en la jerarquia). En CREATE (taskId null) no hay self ni
  // descendientes que excluir. Sin proyecto elegido se ofrecen todas las
  // activas. Se oculta el campo cuando la lista queda vacia (mismo patron que
  // Bloque/Lote/Caso/Modelo).
  const parentOptions = useMemo(() => {
    const selfId = taskId; // null en CREATE
    const descendants = new Set<number>();
    if (selfId != null) {
      const childrenByParent = new Map<number, number[]>();
      for (const t of allTasks) {
        if (t.idTareaPadre != null) {
          const arr = childrenByParent.get(t.idTareaPadre) ?? [];
          arr.push(t.idTareaArq);
          childrenByParent.set(t.idTareaPadre, arr);
        }
      }
      const stack = [selfId];
      while (stack.length) {
        const cur = stack.pop()!;
        for (const c of childrenByParent.get(cur) ?? []) {
          if (!descendants.has(c)) { descendants.add(c); stack.push(c); }
        }
      }
    }
    return allTasks
      .filter(t => t.esActivo !== false)
      .filter(t => t.idTareaArq !== selfId)
      .filter(t => !descendants.has(t.idTareaArq))
      .filter(t => idProyecto == null || t.idProyecto === idProyecto)
      .sort((a, b) => (a.nombreTarea ?? '').localeCompare(b.nombreTarea ?? '', 'es'))
      .map(t => ({ value: t.idTareaArq, label: t.nombreTarea || `#${t.idTareaArq}` }));
  }, [allTasks, taskId, idProyecto]);

  // Nombre auto-concatenado según los campos elegidos + el texto Custom.
  // Reglas (omiten Proyecto/Bloque cuando hay Lote):
  //   con caso : Categoría + IDBD(del caso) + Modelo + Custom
  //   con lote : Categoría + Lote + Custom
  //   sin lote : Categoría + Proyecto(abrev) + Bloque + Custom
  const computedName = useMemo(() => {
    const cat = catalogs?.categories.find(c => c.idCatTareaArq === idCategoria)?.nombre ?? '';
    const lote = catalogs?.lots.find(l => l.idLote === idLote)?.idBd ?? '';
    const caso = catalogs?.cases.find(c => c.idCaso === idCaso);
    const proy = catalogs?.projects.find(p => p.idProyecto === idProyecto)?.abreviatura ?? '';
    const bloque = catalogs?.blocks.find(b => b.idBloque === idBloque)?.bloque ?? '';
    const cust = custom.trim();

    let parts: (string | null | undefined)[];
    if (idCaso !== null) {
      // IDBD del lote del caso (no del idLote del form, que puede no estar set).
      const casoIdBd = (caso?.idLote != null ? idBdByLote.get(caso.idLote) : null) || lote;
      parts = [cat, casoIdBd, caso?.modelo, cust];
    } else if (idLote !== null) {
      parts = [cat, lote, cust];
    } else {
      parts = [cat, proy, bloque, cust];
    }
    return parts.map(p => (p ?? '').trim()).filter(Boolean).join(' ');
  }, [catalogs, idCategoria, idLote, idCaso, idProyecto, idBloque, custom, idBdByLote]);

  // Mantener el Nombre en sync con la concatenación, salvo en modo "nombre libre".
  useEffect(() => {
    if (!nombreLibre) setNombreTarea(computedName);
  }, [computedName, nombreLibre]);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={handleCloseAttempt}>
      <div
        className="bg-white rounded-lg max-w-3xl w-full h-[90vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-200">
          <h2 className="text-base font-semibold">
            {/* Refleja el nombre en vivo (estado), no el guardado, para que el
                título se actualice mientras se arma la concatenación. */}
            {nombreTarea || (isCreating ? 'Nueva tarea' : 'Tarea')}
          </h2>
          <button onClick={handleCloseAttempt} disabled={isBusy} className="p-1 hover:bg-stone-100 rounded disabled:opacity-50">
            <X size={18} weight="bold" />
          </button>
        </div>

        {/* Tabs nav: Detalles / Revisión + boton de Entrega a la derecha. */}
        <div className="flex items-center px-4 border-b border-stone-200 bg-white">
          {([
            { id: 'detalles' as const, label: 'Detalles', icon: Info },
            { id: 'revision' as const, label: 'Revisión', icon: ShieldCheck },
            // Poker solo aparece cuando la tarea ya existe (necesita taskId
            // para registrar votos en el backend). En CREATE no tiene sentido.
            ...(!isCreating ? [{ id: 'poker' as const, label: 'Poker', icon: Trophy }] : []),
          ]).map(t => {
            const Icon = t.icon;
            const isActive = activeTab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setActiveTab(t.id)}
                className={`px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${
                  isActive
                    ? 'border-brand-green-100 text-stone-900 font-semibold'
                    : 'border-transparent text-stone-500 hover:text-stone-900'
                }`}
              >
                <Icon size={14} weight="bold" />
                {t.label}
              </button>
            );
          })}

          {/* Quick-action de Link Entrega. Tiene 3 estados:
              - Con link  -> <a target="_blank"> abre directo
              - Sin link  -> boton "Agregar entrega" abre input inline
              - Editando  -> input + Check/X para aplicar (solo escribe al
                form state; persiste con el Guardar normal del modal) */}
          <div className="ml-auto flex items-center py-1.5">
            {addingLink ? (
              <div className="flex items-center gap-1">
                <input
                  autoFocus
                  type="url"
                  value={tempLink}
                  onChange={e => setTempLink(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      setLinkEntrega(tempLink.trim());
                      setAddingLink(false);
                      setTempLink('');
                    }
                    if (e.key === 'Escape') {
                      setAddingLink(false);
                      setTempLink('');
                    }
                  }}
                  placeholder="https://..."
                  className="bg-stone-100 border-0 rounded px-2 py-1 text-xs w-56 focus:outline-none focus:ring-2 focus:ring-brand-green-100"
                />
                <button
                  type="button"
                  onClick={() => {
                    setLinkEntrega(tempLink.trim());
                    setAddingLink(false);
                    setTempLink('');
                  }}
                  disabled={!tempLink.trim()}
                  title="Aplicar"
                  className="p-1 rounded text-brand-green-200 hover:bg-stone-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Check size={14} weight="bold" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAddingLink(false);
                    setTempLink('');
                  }}
                  title="Cancelar"
                  className="p-1 rounded text-stone-500 hover:bg-stone-100"
                >
                  <X size={14} weight="bold" />
                </button>
              </div>
            ) : linkEntrega ? (
              <a
                href={linkEntrega}
                target="_blank"
                rel="noopener noreferrer"
                title={linkEntrega}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-brand-green-200 hover:bg-stone-100 rounded"
              >
                <LinkSimple size={14} weight="bold" />
                Ver entrega
                <ArrowSquareOut size={12} weight="bold" />
              </a>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setTempLink('');
                  setAddingLink(true);
                }}
                title="Pegar link de entrega"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-brand-gray-400 hover:text-brand-gray-500 hover:bg-stone-100 rounded border border-dashed border-stone-300"
              >
                <Plus size={12} weight="bold" />
                Agregar entrega
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-6">

          {/* ============= TAB DETALLES ============= */}
          {activeTab === 'detalles' && (
          <section className="space-y-3">
            {/* Read-only metadata: shown only when editing an existing task */}
            {!isCreating && task && (
              <div className="px-1 text-xs text-stone-600 space-y-1">
                <div className="flex justify-between items-center">
                  <span>
                    Creado por <strong className="text-stone-700">{task.nomCreador || '—'}</strong>
                  </span>
                  <span className="font-mono text-stone-500">
                    {fmtDate(task.fechaCreacion, true)}
                  </span>
                </div>
                {task.fechaModificacion && (
                  <div className="flex justify-between items-center">
                    <span>
                      Modificado por <strong className="text-stone-700">{task.nomModificador || '—'}</strong>
                    </span>
                    <span className="font-mono text-stone-500">
                      {fmtDate(task.fechaModificacion, true)}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Toggle soft-delete. Gateado a Daniel (IDCol 26) hasta que se
                decida ampliar el rol. Inactiva oculta la tarea del listado
                por defecto en el FilterPanel (Visibilidad = Activas). */}
            {currentUserIdCol === ES_ACTIVO_TOGGLE_IDCOL && (
              <div className="flex items-center justify-between px-1">
                <label className="text-xs font-medium text-stone-700">
                  Visibilidad de la tarea
                </label>
                <SegmentedControl<'active' | 'inactive'>
                  options={[
                    { value: 'active', label: 'Activa' },
                    { value: 'inactive', label: 'Inactiva' },
                  ]}
                  value={esActivo ? 'active' : 'inactive'}
                  onChange={(v) => setEsActivo(v === 'active')}
                />
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-stone-700">Nombre Tarea</label>
                <label className="flex items-center gap-1.5 text-[11px] text-stone-500 cursor-pointer" title="Escribir el nombre completo a mano, sin concatenación">
                  <input
                    type="checkbox"
                    checked={nombreLibre}
                    onChange={e => setNombreLibre(e.target.checked)}
                    className="w-3.5 h-3.5 cursor-pointer accent-brand-green-100"
                  />
                  Nombre libre
                </label>
              </div>
              {nombreLibre ? (
                <input
                  value={nombreTarea}
                  onChange={e => setNombreTarea(e.target.value)}
                  placeholder="Escribí el nombre completo"
                  className="w-full bg-stone-100 border-0 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-brand-green-100"
                />
              ) : (
                <>
                  {/* Preview read-only del nombre concatenado (Categoría/Lote/Caso/… + Custom) */}
                  <div className="w-full px-3 py-2 text-sm text-stone-800 min-h-[2.25rem] break-words">
                    {nombreTarea || <span className="text-stone-400 italic">Se arma con Categoría / Lote / Caso. Activá “Nombre libre” para escribirlo a mano.</span>}
                  </div>
                  {/* El texto personalizado solo aparece cuando hay Categoría: así
                      el campo acompaña a la composición y no se usa como nombre
                      libre antes de elegir la categoría. */}
                  {idCategoria !== null && (
                    <input
                      value={custom}
                      onChange={e => setCustom(e.target.value)}
                      placeholder="Texto personalizado (Custom)…"
                      className="w-full mt-1.5 bg-stone-100 border-0 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-brand-green-100"
                    />
                  )}
                </>
              )}
            </div>

            <div>
              <label className="text-xs font-medium text-stone-700 mb-1 block">Descripción</label>
              <textarea
                value={descripcion} onChange={e => setDescripcion(e.target.value)}
                rows={2}
                className="w-full bg-stone-100 border-0 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-brand-green-100 resize-none"
              />
            </div>

            <SectionDivider label="Clasificación" />

            {/* Categoría + Proyecto: campos de clasificación siempre presentes.
                Junto con Bloque/Lote/Caso/Modelo (abajo) arman el nombre
                auto-concatenado de la tarea. */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-stone-700 mb-1 block">Categoría</label>
                <SearchableSelect<number>
                  options={(catalogs?.categories ?? []).map(c => ({
                    value: c.idCatTareaArq,
                    label: c.nombre || `#${c.idCatTareaArq}`,
                  }))}
                  value={idCategoria}
                  onChange={(v) => setIdCategoria(v)}
                  placeholder="— Ninguna —"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-stone-700 mb-1 block">Proyecto</label>
                <SearchableSelect<number>
                  options={(catalogs?.projects ?? []).map(p => ({
                    value: p.idProyecto,
                    label: p.nombre || `#${p.idProyecto}`,
                  }))}
                  value={idProyecto}
                  onChange={(v) => handleProyectoChange(v)}
                  placeholder="— Ninguno —"
                />
              </div>
            </div>

            {/* Bloque/Lote/Caso/Modelo: solo aparecen cuando el proyecto elegido
                tiene opciones para ese campo (algunos proyectos no manejan
                bloques/lotes/casos). Fluyen en un grid de 2 columnas para que no
                queden huecos cuando alguno se oculta. */}
            <div className="grid grid-cols-2 gap-3">
              {blockOptions.length > 0 && (
                <div>
                  <label className="text-xs font-medium text-stone-700 mb-1 block">Bloque</label>
                  <SearchableSelect<number>
                    options={blockOptions}
                    value={idBloque}
                    onChange={(v) => handleBloqueChange(v)}
                    placeholder="— Ninguno —"
                  />
                </div>
              )}
              {lotOptions.length > 0 && (
                <div>
                  <label className="text-xs font-medium text-stone-700 mb-1 block">Lote</label>
                  <SearchableSelect<number>
                    options={lotOptions}
                    value={idLote}
                    onChange={(v) => handleLoteChange(v)}
                    placeholder="— Ninguno —"
                  />
                </div>
              )}
              {caseOptions.length > 0 && (
                <div>
                  <label className="text-xs font-medium text-stone-700 mb-1 block">Caso</label>
                  <SearchableSelect<number>
                    options={caseOptions.map(c => {
                      const idBd = c.idLote != null ? idBdByLote.get(c.idLote) : null;
                      // Línea 1 (negro): IDBD | Modelo. Línea 2 (gris): Estado | Cliente.
                      const label = [idBd, c.modelo].map(s => s?.trim()).filter(Boolean).join(' | ') || `#${c.idCaso}`;
                      const sublabel = [c.estado, c.cliente].map(s => s?.trim()).filter(Boolean).join(' | ');
                      return { value: c.idCaso, label, sublabel: sublabel || undefined };
                    })}
                    value={idCaso}
                    onChange={(v) => setIdCaso(v)}
                    placeholder="— Ninguno —"
                  />
                </div>
              )}
              {modeloOptions.length > 0 && (
                <div>
                  <label className="text-xs font-medium text-stone-700 mb-1 block">Modelo</label>
                  <SearchableSelect<number>
                    options={modeloOptions}
                    value={idModelo}
                    onChange={(v) => setIdModelo(v)}
                    placeholder="— Ninguno —"
                  />
                </div>
              )}
            </div>

            <SectionDivider label="Gestión" />

            {/* Responsable + Revisor (Autorizado Interno) lado a lado: los dos
                involucrados de la tarea. El Revisor también aparece en el tab
                Revisión (read-only allá; se asigna acá). */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-stone-700 mb-1 block">Responsable</label>
                <SearchableSelect<number>
                  options={(catalogs?.users ?? []).map(u => ({
                    value: u.idCol,
                    label: u.nombreCompleto || u.nombre || `#${u.idCol}`,
                  }))}
                  value={responsable}
                  onChange={(v) => setResponsable(v)}
                  placeholder="— Sin asignar —"
                />
              </div>
              <div>
                <AutorizadoInternoField
                  users={catalogs?.users ?? []}
                  idAutorizado={idAutorizadoInt}
                  onIdAutorizadoChange={setIdAutorizadoInt}
                  fechaAutorizado={fechaAutorizadoInt}
                  onFechaAutorizadoChange={setFechaAutorizadoInt}
                  nomAutorizado={task?.nomAutorizado}
                  hideDate
                  readOnly={esHielera}
                />
                {esHielera && (
                  <p className="mt-1 text-[10px] text-stone-400">
                    Las tareas en Hielera no llevan revisor.
                  </p>
                )}
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-stone-700 mb-1 block">Estado</label>
              <StatusSelect
                statuses={catalogs?.statuses ?? []}
                value={idEstado}
                onChange={(id) => setIdEstado(id)}
              />
            </div>

            {/* Tarea padre: arma la jerarquia que agrupa las barras en el Gantt.
                Candidatos = tareas activas del mismo proyecto (excluye self +
                descendientes para no permitir ciclos). Oculto si no hay candidatos. */}
            {parentOptions.length > 0 && (
              <div>
                <label className="text-xs font-medium text-stone-700 mb-1 block">Tarea padre</label>
                <SearchableSelect<number>
                  options={parentOptions}
                  value={idTareaPadre}
                  onChange={(v) => setIdTareaPadre(v)}
                  placeholder="— Ninguna (tarea raíz) —"
                />
              </div>
            )}

            {/* Fechas. "Inicio Planeado" + "Fecha Proyectada" son el par
                PLANEADO (de donde el Gantt dibuja la barra de lo no empezado);
                "Fecha Inicio" es el inicio REAL. */}
            <div className="grid grid-cols-2 gap-3">
              <DateFieldWithClear label="Inicio Planeado" value={fechaProyectadaIn} onChange={setFechaProyectadaIn} />
              <DateFieldWithClear label="Fecha Proyectada" value={fechaProyectada} onChange={setFechaProyectada} />
              <DateFieldWithClear label="Fecha Inicio" value={fechaInicio} onChange={setFechaInicio} />
            </div>

            {showFechaCompletado && (
              <div>
                <DateFieldWithClear
                  label="Fecha Completado"
                  value={fechaCompletado}
                  onChange={setFechaCompletado}
                  error={fechaCompletadoMissing}
                  errorMessage={fechaCompletadoMissing ? 'Requerida en este estado' : undefined}
                />
              </div>
            )}

            {/* Prioridad + Sprint + Horas estimadas. El sprint default en CREATE
                es el semanal vigente (se resuelve en el effect de defaults).
                Horas: numerico, step 0.25 para fracciones de hora; los
                resultados del poker (F5) van a llenar este campo. */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-stone-700 mb-1 block">Prioridad</label>
                <SearchableSelect<number>
                  options={(catalogs?.priorities ?? []).map(p => ({
                    value: p.id,
                    label: p.nombre,
                  }))}
                  value={prioridad}
                  onChange={(v) => setPrioridad(v)}
                  placeholder="—"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-stone-700 mb-1 block">Sprint</label>
                <SearchableSelect<number>
                  options={(catalogs?.sprints ?? []).map(s => ({
                    value: s.idSprint,
                    label: s.nombre,
                  }))}
                  value={idSprint}
                  onChange={(v) => setIdSprint(v)}
                  placeholder="— Sin Asignar —"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-stone-700 mb-1 block">Horas estimadas</label>
                <input
                  type="number"
                  min={0}
                  step={0.25}
                  value={horasEstimadas}
                  onChange={e => setHorasEstimadas(e.target.value)}
                  placeholder="0.0"
                  className="w-full bg-stone-100 border-0 rounded px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-stone-700 mb-1 block">Link Entrega</label>
              <input
                value={linkEntrega} onChange={e => setLinkEntrega(e.target.value)}
                className="w-full bg-stone-100 border-0 rounded px-3 py-2 text-sm"
              />
            </div>

            {/* Solicitante + N° Solicitud ocultos por ahora (request del
                usuario). El estado se mantiene; en edición preserva el valor
                existente. Cambiar a true para volver a mostrarlos. */}
            {false && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-stone-700 mb-1 block">Solicitante</label>
                <SearchableSelect<number>
                  options={(catalogs?.users ?? []).map(u => ({
                    value: u.idCol,
                    label: u.nombreCompleto || u.nombre || `#${u.idCol}`,
                  }))}
                  value={idSolicitante}
                  onChange={(v) => setIdSolicitante(v)}
                  placeholder="— Ninguno —"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-stone-700 mb-1 block">N° Solicitud</label>
                <input
                  type="number"
                  value={idSolicitud} onChange={e => setIdSolicitud(e.target.value)}
                  className="w-full bg-stone-100 border-0 rounded px-3 py-2 text-sm"
                />
              </div>
            </div>
            )}

            {/* Checklist normal (solo edit). Items con tipo='revision' viven
                en el tab Revisión, NO aparecen aca. */}
            {!isCreating && (
              <div className="border-t border-stone-200 pt-4 mt-4">
                <h3 className="text-sm font-semibold text-stone-700 mb-3 flex items-center gap-1.5">
                  <CheckSquare size={14} weight="bold" /> Checklist
                </h3>
                <ChecklistList
                  items={checklistNormal}
                  onToggle={toggleItem}
                  onDelete={deleteItem}
                />
                <div className="mt-2 flex items-center gap-4 flex-wrap">
                  <button
                    onClick={() => addChecklistItemOfType('checklist')}
                    className="text-xs flex items-center gap-1 text-brand-gray-400 hover:text-brand-gray-500"
                  >
                    <Plus size={12} weight="bold" /> Agregar
                  </button>
                  {taskId !== null && (
                    <>
                      <LoadTemplateButton
                        categoryId={idCategoria}
                        tipo="checklist"
                        taskId={taskId}
                        onLoaded={() => {
                          qc.invalidateQueries({ queryKey: ['checklist', taskId] });
                          qc.invalidateQueries({ queryKey: ['tasks'] });
                        }}
                      />
                      <SaveAsTemplateButton
                        taskId={taskId}
                        tipo="checklist"
                        defaultCategoryId={idCategoria}
                        defaultName={`${task?.nombreTarea || 'Tarea'} – Checklist`}
                        hasItems={checklistNormal.length > 0}
                      />
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Preview read-only de la Observación de revisión. El Revisor y su
                estado ya se ven arriba (Gestión); acá solo asomamos el texto de
                la observación, que se edita en el tab Revisión. */}
            {!isCreating && observacion && (
              <div className="border-t border-stone-200 pt-4 mt-4">
                <h3 className="text-sm font-semibold text-stone-700 mb-3 flex items-center gap-1.5">
                  <ShieldCheck size={14} weight="bold" /> Observación de revisión
                  <span className="text-stone-400 font-normal text-[10px]">
                    (solo vista — editá en el tab Revisión)
                  </span>
                </h3>
                <div className="bg-stone-50 rounded px-3 py-2 text-xs text-stone-700 whitespace-pre-wrap">
                  {observacion}
                </div>
              </div>
            )}

            {/* Actividad: timeline unificado (creación + comentarios + cambios
                de campos auditados). Reemplaza la bitácora plana de antes.
                Sticky comment bar abajo permite agregar comentarios sin
                cambiar de tab — quedan inyectados en el timeline al refrescar. */}
            {!isCreating && taskId !== null && (
              <div className="border-t border-stone-200 pt-4 mt-4">
                <h3 className="text-sm font-semibold text-stone-700 mb-3 flex items-center gap-1.5">
                  <ChatText size={14} weight="bold" /> Actividad
                </h3>
                <TimelineForTask taskId={taskId} />
              </div>
            )}
          </section>
          )}

          {/* ============= TAB REVISIÓN ============= */}
          {activeTab === 'revision' && (
          <section className="space-y-3">
            {/* El Revisor se asigna en Detalles; acá solo se muestra (read-only)
                como contexto + la fecha de autorización, que sí se edita acá. */}
            <div className="grid grid-cols-2 gap-3">
              <AutorizadoInternoField
                users={catalogs?.users ?? []}
                idAutorizado={idAutorizadoInt}
                onIdAutorizadoChange={setIdAutorizadoInt}
                fechaAutorizado={fechaAutorizadoInt}
                onFechaAutorizadoChange={setFechaAutorizadoInt}
                nomAutorizado={task?.nomAutorizado}
                hideDate
                readOnly
              />
              <DateFieldWithClear
                label="Fecha Autorizado"
                value={fechaAutorizadoInt}
                onChange={setFechaAutorizadoInt}
              />
            </div>

            <div>
              <label className="text-xs font-medium text-stone-700 mb-1 block flex items-center gap-1.5">
                <span>Observación</span>
                {!canEditObservacion && (
                  <span className="text-stone-400 font-normal text-[10px]">
                    (solo Autorizado Interno)
                  </span>
                )}
              </label>
              <textarea
                value={observacion}
                onChange={e => setObservacion(e.target.value)}
                rows={3}
                disabled={!canEditObservacion}
                title={canEditObservacion ? '' : observacionDisabledReason}
                className={`w-full border-0 rounded px-3 py-2 text-sm resize-none ${
                  canEditObservacion
                    ? 'bg-stone-100 focus:ring-2 focus:ring-brand-green-100'
                    : 'bg-stone-50 text-stone-500 cursor-not-allowed'
                }`}
              />
            </div>

            {/* Checklist de revisión: visible para todos, editable solo
                por el Autorizado Interno asignado. Items son ChecklistARQ
                con tipo='revision'. */}
            {!isCreating && (
              <div className="border-t border-stone-200 pt-4 mt-4">
                <h3 className="text-sm font-semibold text-stone-700 mb-3 flex items-center gap-1.5">
                  <CheckSquare size={14} weight="bold" /> Checklist de Revisión
                  {!canEditObservacion && (
                    <span className="text-stone-400 font-normal text-[10px]">
                      (solo Autorizado Interno edita)
                    </span>
                  )}
                </h3>
                {checklistRevision.length === 0 && (
                  <p className="text-xs text-stone-500 italic mb-2">
                    Sin items de revisión.
                  </p>
                )}
                <ChecklistList
                  items={checklistRevision}
                  onToggle={toggleItem}
                  onDelete={deleteItem}
                  readOnly={!canEditObservacion}
                />
                {canEditObservacion && (
                  <div className="mt-2 flex items-center gap-4 flex-wrap">
                    <button
                      onClick={() => addChecklistItemOfType('revision')}
                      className="text-xs flex items-center gap-1 text-brand-gray-400 hover:text-brand-gray-500"
                    >
                      <Plus size={12} weight="bold" /> Agregar
                    </button>
                    {taskId !== null && (
                      <>
                        <LoadTemplateButton
                          categoryId={idCategoria}
                          tipo="revision"
                          taskId={taskId}
                          onLoaded={() => {
                            qc.invalidateQueries({ queryKey: ['checklist', taskId] });
                            qc.invalidateQueries({ queryKey: ['tasks'] });
                          }}
                        />
                        <SaveAsTemplateButton
                          taskId={taskId}
                          tipo="revision"
                          defaultCategoryId={idCategoria}
                          defaultName={`${task?.nombreTarea || 'Tarea'} – Revisión`}
                          hasItems={checklistRevision.length > 0}
                        />
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </section>
          )}

          {/* ============= TAB POKER ============= */}
          {activeTab === 'poker' && !isCreating && taskId !== null && (
          <section>
            <PokerPanel
              taskId={taskId}
              onMedianaAplicada={(horas) => setHorasEstimadas(String(horas))}
            />
          </section>
          )}

        </div>

        <StickyCommentBar
          value={newComment}
          onChange={setNewComment}
          onPost={postComment}
          disabled={isCreating}
          disabledReason="Guardá la tarea primero"
        />

        {errorMsg && (
          <div className="px-4 py-2 bg-red-50 border-t border-red-200 text-sm text-red-700 flex items-center justify-between">
            <span>⚠ {errorMsg}</span>
            <button onClick={() => setErrorMsg(null)} className="text-red-700 hover:text-red-900">
              <X size={14} weight="bold" />
            </button>
          </div>
        )}

        <div className="flex items-center justify-between px-4 py-3 border-t border-stone-200 bg-stone-100">
          <div>
            {!isCreating && (
              <button
                onClick={handleDelete}
                disabled={isBusy}
                className="px-3 py-2 text-sm border border-red-300 text-red-700 rounded hover:bg-red-50 flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeleting ? <CircleNotch size={14} weight="bold" className="animate-spin" /> : <Trash size={14} weight="bold" />}
                {isDeleting ? 'Eliminando…' : 'Eliminar'}
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            {isDirty && (
              <span className="text-xs text-amber-800 font-semibold bg-amber-100 border border-amber-300 rounded px-2 py-1 flex items-center gap-1.5">
                <Warning size={14} weight="bold" />
                Cambios sin guardar
              </span>
            )}
            <button
              onClick={handleSave}
              disabled={isBusy || isInvalid}
              title={isInvalid ? 'Completá la fecha de completado para guardar' : ''}
              className="px-4 py-2 text-sm bg-brand-green-100 text-black font-semibold rounded hover:bg-brand-green-200 flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-not-allowed min-w-[110px] justify-center"
            >
              {isSaving ? (
                <>
                  <CircleNotch size={14} weight="bold" className="animate-spin" />
                  {isCreating ? 'Creando…' : 'Guardando…'}
                </>
              ) : (
                <>
                  <FloppyDisk size={14} weight="bold" />
                  {isCreating ? 'Crear tarea' : 'Guardar'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// ChecklistList - render compartido por la seccion Checklist
// del tab Detalles y la seccion Checklist Revision del tab Revision.
// readOnly oculta el boton de borrar y desactiva el toggle de completado.
// ============================================================
type ChecklistListProps = {
  items: Array<{
    idChecklistArq: number;
    texto?: string | null;
    completado?: boolean | null;
  }>;
  onToggle: (itemId: number, completado: boolean) => void;
  onDelete: (itemId: number) => void;
  readOnly?: boolean;
};

// ============================================================
// LoadTemplateButton - boton "Cargar plantilla" con popover.
// Filtra plantillas por categoria + tipo. Si no hay categoria o no
// hay plantillas para esa combinacion, no renderiza nada.
// Click abre menu con la lista; click en una plantilla la carga
// (POST load-template) y dispara onLoaded para refrescar checklist.
// ============================================================
type LoadTemplateButtonProps = {
  categoryId: number | null;
  tipo: ChecklistTipo;
  taskId: number;
  onLoaded: () => void;
};

function LoadTemplateButton({ categoryId, tipo, taskId, onLoaded }: LoadTemplateButtonProps) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { data: templates = [] } = useQuery({
    queryKey: ['plantillas', categoryId, tipo, true],
    queryFn: () => api.plantillas.list({ categoryId: categoryId!, tipo, activo: true }),
    enabled: categoryId !== null,
  });

  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  if (categoryId === null) return null;
  if (templates.length === 0) return null;

  async function loadTemplate(plantillaId: number) {
    if (busy) return;
    setBusy(true);
    try {
      // Pasamos siempre el tipo destino del callsite (checklist o revision).
      // El backend lo ignora salvo cuando la plantilla origen es 'ambas',
      // donde es obligatorio para definir como instanciar los items.
      await api.checklist.loadTemplate(taskId, plantillaId, { tipo });
      onLoaded();
      setOpen(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        disabled={busy}
        className="text-xs flex items-center gap-1 text-brand-gray-400 hover:text-brand-gray-500 disabled:opacity-50"
      >
        <Files size={12} weight="bold" />
        {busy ? 'Cargando…' : 'Cargar plantilla'}
        <CaretDown size={10} weight="bold" />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-20 min-w-[220px] bg-white border border-stone-200 rounded shadow-lg py-1 max-h-64 overflow-auto">
          {templates.map(t => (
            <button
              key={t.idPlantillaTarea}
              type="button"
              onClick={() => loadTemplate(t.idPlantillaTarea)}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-stone-100 flex justify-between items-center gap-3"
            >
              <span className="truncate text-stone-800">{t.nombre}</span>
              <span className="text-stone-400 flex-shrink-0">{t.itemCount} items</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// SaveAsTemplateButton - "Guardar como plantilla" para checklists.
// Abre un mini-dialog con nombre + categoria + (tipo fijo segun el
// callsite). Llama a /api/tasks/{id}/checklist/save-as-template,
// que toma los items del Tipo dado y crea una plantilla nueva.
// Disabled cuando no hay items (no aporta).
// ============================================================
type SaveAsTemplateButtonProps = {
  taskId: number;
  tipo: ChecklistTipo;
  defaultCategoryId: number | null;
  defaultName: string;
  hasItems: boolean;
};

function SaveAsTemplateButton({
  taskId, tipo, defaultCategoryId, defaultName, hasItems,
}: SaveAsTemplateButtonProps) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();

  if (!hasItems) {
    return (
      <button
        type="button"
        disabled
        title="No hay items para guardar como plantilla"
        className="text-xs flex items-center gap-1 text-stone-300 cursor-not-allowed"
      >
        <FloppyDiskBack size={12} weight="bold" />
        Guardar como plantilla
      </button>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-xs flex items-center gap-1 text-brand-gray-400 hover:text-brand-gray-500"
      >
        <FloppyDiskBack size={12} weight="bold" />
        Guardar como plantilla
      </button>
      {open && (
        <SaveAsTemplateDialog
          taskId={taskId}
          tipo={tipo}
          defaultCategoryId={defaultCategoryId}
          defaultName={defaultName}
          onClose={() => setOpen(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['plantillas'] });
            setOpen(false);
          }}
        />
      )}
    </>
  );
}

function SaveAsTemplateDialog({
  taskId, tipo, defaultCategoryId, defaultName, onClose, onSaved,
}: {
  taskId: number;
  tipo: ChecklistTipo;
  defaultCategoryId: number | null;
  defaultName: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { data: catalogs } = useCatalogs();
  const [nombre, setNombre] = useState(defaultName);
  const [idCategoria, setIdCategoria] = useState<number | null>(defaultCategoryId);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = nombre.trim().length > 0 && idCategoria !== null;

  async function handleSubmit() {
    if (!canSubmit || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.checklist.saveAsTemplate(taskId, {
        nombre: nombre.trim(),
        idCategoria: idCategoria!,
        tipo,
      });
      onSaved();
    } catch (err: any) {
      setError(err?.message || 'Error al guardar la plantilla');
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg w-full max-w-md p-5 space-y-4"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold flex items-center gap-2">
            <FloppyDiskBack size={16} weight="bold" />
            Guardar como plantilla {tipo === 'revision' ? '(Revisión)' : '(Checklist)'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-stone-100 rounded">
            <X size={18} weight="bold" />
          </button>
        </div>

        <p className="text-xs text-stone-500">
          Se va a crear una plantilla nueva con los items {tipo === 'revision' ? 'de revisión' : 'del checklist'} de esta tarea.
          Después podés editarla en la pestaña Plantillas.
        </p>

        <div>
          <label className="text-xs font-medium text-stone-600 mb-1 block">Nombre</label>
          <input
            autoFocus
            value={nombre}
            onChange={e => setNombre(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && canSubmit) handleSubmit(); }}
            className="w-full bg-stone-100 border-0 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-brand-green-100"
          />
        </div>

        <div>
          <label className="text-xs font-medium text-stone-600 mb-1 block">Categoría</label>
          <SearchableSelect<number>
            options={(catalogs?.categories ?? []).map(c => ({
              value: c.idCatTareaArq,
              label: c.nombre || `#${c.idCatTareaArq}`,
            }))}
            value={idCategoria}
            onChange={setIdCategoria}
            placeholder="— Seleccionar —"
            clearable={false}
          />
        </div>

        {error && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
            ⚠ {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="px-3 py-1.5 text-sm border border-stone-300 rounded hover:bg-stone-50 disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || busy}
            className="px-4 py-1.5 text-sm bg-brand-green-100 text-black font-semibold rounded hover:bg-brand-green-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            {busy ? (
              <>
                <CircleNotch size={12} weight="bold" className="animate-spin" />
                Guardando…
              </>
            ) : 'Guardar plantilla'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ChecklistList({ items, onToggle, onDelete, readOnly = false }: ChecklistListProps) {
  if (items.length === 0) return null;
  return (
    <div className="space-y-2.5">
      {items.map(item => (
        <div key={item.idChecklistArq} className="flex items-start gap-2.5 text-sm group">
          <input
            type="checkbox"
            checked={!!item.completado}
            onChange={e => onToggle(item.idChecklistArq, e.target.checked)}
            disabled={readOnly}
            className="mt-0.5 w-4 h-4 cursor-pointer accent-brand-green-100 disabled:cursor-not-allowed"
          />
          <span className={`flex-1 ${item.completado ? 'line-through text-stone-400' : ''}`}>
            {item.texto}
          </span>
          {!readOnly && (
            <button
              type="button"
              onClick={() => onDelete(item.idChecklistArq)}
              className="opacity-0 group-hover:opacity-100 text-stone-400 hover:text-red-600 transition-opacity p-0.5 rounded hover:bg-stone-100"
              title="Eliminar item"
              aria-label="Eliminar item"
            >
              <X size={12} weight="bold" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

// ============================================================
// TimelineForTask - wrapper que carga useTaskTimeline y renderiza
// TimelineList. Aislado en su propio componente para que el hook no
// se monte mientras estamos en CREATE (no hay taskId).
// ============================================================
function TimelineForTask({ taskId }: { taskId: number }) {
  const { data: eventos = [], isLoading } = useTaskTimeline(taskId);
  if (isLoading) return <p className="text-xs text-stone-500 italic">Cargando actividad…</p>;
  return <TimelineList eventos={eventos} emptyMessage="Sin actividad todavía." />;
}